import type { ISbRichtext } from "gatsby-source-storyblok";
import { useEffect, useState, type FC } from "react";
import { nonEmptyArray } from "../../utils/type-guards/general";
import Button from "../general/Button";
import RichText from "../general/RichText/RichText";

import * as s from "./ComparisonTable.module.scss";
import ComparisonItem, {
	type ComparisonItemProps,
} from "./components/ComparisonItem/ComparisonItem";
import ComparisonProductCard from "./components/ComparisonProductCard/ComparisonProductCard";

type ComparisonTableProps = {
	headline: string;
	richtext: string | ISbRichtext;
	items: ComparisonItemProps[];
	compareMore: { label: string; link: string };
	label?: string;
};

const ComparisonTable: FC<ComparisonTableProps> = ({
	headline,
	richtext,
	items,
	compareMore,
	label,
}) => {
	const [renderedItems, setRenderedItems] = useState<ComparisonItemProps[]>([
		...items,
	]);
	const [openDrawer, setOpenDrawer] = useState<string | null>(null);

	useEffect(() => {
		setRenderedItems([...items]);

		const resizeHandler = () => {
			setRenderedItems([...items]);
		};

		window.addEventListener("resize", resizeHandler);

		return window.removeEventListener("resize", resizeHandler);
	}, [items]);

	const changeItemHandler = (index: number, uid: string) => {
		const itemToMove = renderedItems.find((i) => i.uid === uid);

		if (itemToMove) {
			const newArr = renderedItems.filter((i) => i.uid !== uid);
			const replacedItem = newArr.splice(index, 1, itemToMove);
			if (nonEmptyArray(replacedItem)) newArr.push(replacedItem[0]);

			setRenderedItems([...newArr]);
		}
	};

	if (renderedItems.length === 0) return null;

	return (
		<div className={s.container}>
			<div className={s.text}>
				<div className={s.headline}>
					{label && <label>{label}</label>}
					<h2>{headline}</h2>
				</div>
				<RichText content={richtext} className={s.richText} />
			</div>
			<div className={s.cardWrapper}>
				{renderedItems.map((item, index) => (
					<ComparisonProductCard
						key={item.uid}
						items={items}
						index={index}
						uid={item.uid}
						image={{ alt: item.image?.alt ?? "", src: item.image?.src ?? "" }}
						onChange={changeItemHandler}
						openDrawer={openDrawer}
						setOpenDrawer={setOpenDrawer}
						productName={item.productName}
					/>
				))}
			</div>
			<table className={s.table}>
				<tbody>
					{renderedItems[0]?.content.map((item) => (
						<ComparisonItem
							key={item.uid}
							label={item.label}
							items={renderedItems}
						/>
					))}
				</tbody>
			</table>

			<div className={s.buttonWrapper}>
				{renderedItems.map((item) => (
					<div key={item.uid}>
						<Button
							variant="secondary"
							label={item.readMore.label}
							href={item.readMore.link}
						/>
					</div>
				))}
			</div>
			<span className={s.compareMore}>
				<Button
					variant="no-style"
					label={compareMore.label}
					href={compareMore.link}
				/>
			</span>
		</div>
	);
};

export default ComparisonTable;
